import {Button, Radio} from 'antd';
import {ExtractRouteParams, useParams} from 'react-router';

import {appRoute} from '../../../../../app-route';
import {AlertFallback} from '../../../../../component/alert-fallback/alert-fallback';
import {Meta} from '../../../../../component/meta/meta';
import {BreadCrumbs} from '../../../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../../../layout/page/page';
import {MainPageContainer} from '../../../../../layout/page-card/main-page-card';
import {PageCard} from '../../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../../layout/page-header/page-header';
import {Spinner} from '../../../../../layout/spinner/spinner';
import {Locale} from '../../../../../provider/locale/locale';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {useGoBackHook} from '../../../../../service/navigation/go-back-hook';
import {useCatalogsByGroup} from '../../../../../service/source-settings/catalogs/catalogs-hook';
import {CatalogByGroupType} from '../../../../../service/source-settings/catalogs/catalogs-type';
import {SourceLinkTypeEnum} from '../../../../../service/source-settings/source-links/source-links-type';
import {LinksTable} from '../source-links-page/source-content/links-table/links-table';

import {FacebookNoAccountPlaceholder} from './facebook-no-account-placeholder/facebook-no-account-placeholder';
import {HeaderAlert} from './header-alert/header-alert';
import {isBrandOnlyReviewsCatalogId, useCatalogHeaderAlertConfig} from './header-alert/header-alert-hook';
import {useCatalogLinkType} from './use-catalog-link-type';
import {useFacebookCatalogLinksGate} from './use-facebook-catalog-links-gate';
import * as styles from './source-catalog-page.scss';

export function SourceCatalogPage(): JSX.Element {
    const {getLocalizedString} = useLocale();
    const {id, groupId} = useParams<
        ExtractRouteParams<typeof appRoute.settingsSourceSettingsCatalog.path, string>
    >() || {id: '', groupId: ''};

    const goBack = useGoBackHook(appRoute.settingsSourceSettings.path);

    const {result, isInProgress, processError} = useCatalogsByGroup(Number(groupId));

    const activeCatalog: CatalogByGroupType | undefined = result
        ?.find((group) => String(group.id) === groupId)
        ?.catalogs.find((catalog) => String(catalog.id) === id);

    const {
        facebookAccountsProcessError,
        isFacebookAccountsInProgress,
        isFacebookNoAccountPlaceholderVisible,
        isLinksTableVisible,
    } = useFacebookCatalogLinksGate(Number(id), Boolean(activeCatalog));

    const isContentLoading = isInProgress || isFacebookAccountsInProgress;

    const contentProcessError = processError || facebookAccountsProcessError;

    const headerAlertConfig = useCatalogHeaderAlertConfig(id);

    const {linkType, onLinkTypeSelect} = useCatalogLinkType(activeCatalog);

    return (
        <Page renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <Meta title={getLocalizedString('CATEGORY_NAME__SOURCE_SETTINGS')} />

            <PageCard>
                <BreadCrumbs
                    list={[
                        {
                            path: appRoute.settingsSourceSettings.path,
                            titleLangKey: 'CATEGORY_NAME__SOURCE_SETTINGS',
                        },
                        {
                            path: appRoute.settingsSourceSettings.path,
                            title: activeCatalog?.name,
                        },
                    ]}
                />

                <PageHeader>
                    {activeCatalog?.name}

                    <Button className={styles.SourceCatalogPage_button} onClick={goBack}>
                        <Locale stringKey="BUTTON__BACK" />
                    </Button>
                </PageHeader>
                {headerAlertConfig && (
                    <HeaderAlert
                        linkHref={headerAlertConfig.linkHref}
                        linkStringKey={headerAlertConfig.linkStringKey}
                        messageStringKey={headerAlertConfig.messageStringKey}
                    />
                )}
            </PageCard>

            <PageCard>
                <Spinner isShow={isContentLoading} position="absolute" />

                {contentProcessError && <AlertFallback />}

                {isFacebookNoAccountPlaceholderVisible && <FacebookNoAccountPlaceholder />}

                {isLinksTableVisible && activeCatalog && (
                    <LinksTable
                        catalog={activeCatalog}
                        catalogGroupId={Number(groupId)}
                        key={`${activeCatalog.id}=${linkType}`}
                        linkType={linkType}
                        linkTypeControl={
                            <Radio.Group onChange={onLinkTypeSelect} value={linkType}>
                                <Radio.Button value={SourceLinkTypeEnum.Brand}>
                                    <Locale stringKey="SOURCE_SETTINGS__LINKS__LINK_TYPE_BRAND" />
                                </Radio.Button>
                                {!isBrandOnlyReviewsCatalogId(activeCatalog.id) ? (
                                    <Radio.Button value={SourceLinkTypeEnum.Company}>
                                        <Locale stringKey="SOURCE_SETTINGS__LINKS__LINK_TYPE_COMPANY" />
                                    </Radio.Button>
                                ) : null}
                            </Radio.Group>
                        }
                    />
                )}
            </PageCard>
        </Page>
    );
}
